/**
 * A game: a session of caves (docs/spec/02 §9, 07 §1). Starts each cave, moves the session on when
 * one ends, shows level-complete and game-over, pauses, and hands scores to the tables and records.
 */

import { buildCave } from '../../caves/build';
import type { LoggedGame } from '../../game/gameLog';
import { GameRecorder } from '../../game/gameLog';
import type { CaveOutcome, Next, Session } from '../../game/session';
import { addScore, attemptSeed, currentPlayer, endCave, startSession } from '../../game/session';
import type { DirectionLatch } from '../../input/latch';
import { WHITE, YELLOW } from '../../render/palette';
import type { Screen } from '../../render/screen';
import type { CaveState, Input } from '../../sim';
import type { MenuAction, PlayRequest, PointerInput, Scene, SceneHost, UiAudio } from '../../ui/scene';
import { CaveRun } from './caveRun';
import type { CaveTurn } from './caveSource';
import { caveForTurn, startPosition } from './caveSource';
import { finishGame, finishOneShot } from './finish';
import { drawBanner, drawRun, gameWidth } from './gameView';
import type { PauseItem } from './pauseMenu';
import { drawPause, pauseHit, pauseItems } from './pauseMenu';
import type { RunAudio } from './runAudio';

export interface GameDeps {
  readonly audio: UiAudio & RunAudio;
  readonly latch: DirectionLatch;
  /** The autopilot for the attract demo. */
  readonly pilot?: () => (s: CaveState) => Input;
  /** Touch controls on or off while this scene is on top. */
  readonly touch?: (visible: boolean) => void;
}

const BANNER_MS = 3200;

/** The games whose scores can go online, as their log names them. */
function loggedGame(r: PlayRequest): LoggedGame | null {
  if (r.mode === 'classic' || r.mode === 'deep') return { mode: r.mode, players: r.players, startCave: r.startCave, level: r.level };
  if (r.mode === 'expedition') return { mode: 'expedition', seed: r.seed };
  if (r.mode === 'daily') return { mode: 'daily', year: r.year, month: r.month, day: r.day };
  return null;
}

export class GameScene implements Scene {
  private session: Session;
  private turn!: CaveTurn;
  private run!: CaveRun;
  private scoreBase = 0;
  private paused = false;
  private pauseIndex = 0;
  private readonly items: PauseItem[];
  private banner: { text: string; sub: string; ms: number; then: () => void } | null = null;
  private forced: CaveOutcome | null = null;
  private readonly drive: ((s: CaveState) => Input) | null;
  /** Every input of every attempt, so an online board can replay the game to check its scores. */
  private readonly recorder: GameRecorder | null;

  constructor(private readonly host: SceneHost, readonly request: PlayRequest, private readonly deps: GameDeps) {
    const r = request;
    this.session = startSession(
      r.mode === 'classic' || r.mode === 'deep'
        ? { mode: r.mode, players: r.players, position: startPosition(r.startCave), level: r.level }
        : { mode: r.mode },
    );
    this.items = pauseItems(!this.session.unlimited);
    this.drive = r.mode === 'demo' && deps.pilot ? deps.pilot() : null;
    const logged = loggedGame(r);
    this.recorder = logged ? new GameRecorder(logged) : null;
    this.startCave();
  }

  /** For window.__game.cave(): the numbers a test wants to see. */
  debugState(): Record<string, unknown> {
    const s = this.run.state;
    const p = currentPlayer(this.session);
    return {
      cave: this.turn.def.id, name: this.turn.def.name, level: this.turn.level, phase: this.run.phase, status: s.status,
      scan: s.scan, diamonds: s.diamonds, quota: s.params.diamondsNeeded, timeLeft: s.timeLeft, gateOpen: s.gateOpen,
      digby: { ...s.digby }, lives: p.lives, score: p.score, paused: this.paused, banner: this.banner?.text ?? null,
    };
  }

  size(): { w: number; h: number } {
    return { w: gameWidth(this.host.services.options.get()), h: 200 };
  }

  enter(): void {
    this.deps.touch?.(this.request.mode !== 'demo');
    this.playMusic();
  }

  leave(): void {
    this.deps.touch?.(false);
    this.deps.audio.ambience({ amoeba: false, magic: false, cover: false });
  }

  private playMusic(): void {
    const o = this.host.services.options.get();
    if (o.caveMusic && this.request.mode !== 'demo') this.deps.audio.music('cave', this.turn.set === 'deep' ? 'deep' : 'classic');
    else this.deps.audio.music(null);
  }

  private startCave(): void {
    this.turn = caveForTurn(this.request, this.session);
    const built = buildCave(this.turn.def, this.turn.level);
    this.scoreBase = currentPlayer(this.session).score;
    this.forced = null;
    this.run = new CaveRun(built, attemptSeed(this.session, built.cells.length + this.turn.level), {
      audio: this.deps.audio,
      options: () => this.host.services.options.get(),
      score: (points) => {
        const { session, bonusLives } = addScore(this.session, points);
        this.session = session;
        return this.session.unlimited ? 0 : bonusLives;
      },
      input: (s) => {
        const input = this.drive ? this.drive(s) : this.deps.latch.take();
        return this.recorder ? this.recorder.input(input) : input;
      },
      announce: (text) => this.host.services.announce(text),
    });
    this.run.viewW = this.size().w;
    this.run.snapCamera();
    this.host.services.announce(`${this.turn.def.name}. ${this.turn.def.hint}`);
  }

  update(dtMs: number): void {
    if (this.banner) {
      this.banner.ms -= dtMs;
      this.run.update(dtMs);
      if (this.banner.ms <= 0) {
        const then = this.banner.then;
        this.banner = null;
        then();
      }
      return;
    }
    if (this.paused) return;
    this.run.update(dtMs);
    if (this.run.phase === 'done') this.caveOver(this.forced ?? this.run.outcome);
  }

  private caveOver(outcome: CaveOutcome): void {
    const turn = this.turn;
    const secs = this.run.state.bornMs >= 0 ? Math.floor((this.run.state.ms - this.run.state.bornMs) / 1000) : 0;
    const cave = { outcome, score: currentPlayer(this.session).score - this.scoreBase, seconds: secs, diamonds: this.run.state.diamonds };
    this.recorder?.endAttempt(this.forced ? 'restart' : 'cave');
    const { session, next } = endCave(this.session, outcome, turn.def.intermission);
    this.session = session;
    this.afterCave(next, cave);
  }

  private afterCave(next: Next, cave: { outcome: CaveOutcome; score: number; seconds: number; diamonds: number }): void {
    const services = this.host.services;
    if (next === 'finished') return void finishOneShot(this.host, this.request, this.turn, cave, this.recorder?.text());
    if (next === 'gameOver') {
      this.deps.audio.music('gameover');
      this.banner = { text: 'GAME OVER', sub: this.finalScores(), ms: BANNER_MS, then: () => this.finish() };
      services.announce('Game over');
      return;
    }
    if (next === 'levelComplete') {
      this.deps.audio.music('complete');
      const level = currentPlayer(this.session).level;
      this.banner = { text: `LEVEL ${Math.max(1, level - 1)} COMPLETE`, sub: `ON TO LEVEL ${level}`, ms: BANNER_MS, then: () => this.continueGame() };
      return;
    }
    this.continueGame();
  }

  private finish(): void {
    finishGame(this.host, this.request, this.session, (p) => this.labelFor(p), this.recorder?.text());
  }

  private finalScores(): string {
    return this.session.players.map((p, i) => (this.session.players.length > 1 ? `P${i + 1} ` : 'SCORE ') + String(p.score).padStart(6, '0')).join('   ');
  }

  /** The window lost focus in the middle of a cave: stop the clock. */
  autoPause(): void {
    if (!this.paused && !this.banner && this.request.mode !== 'demo' && this.run.phase === 'play') this.setPaused(true);
  }

  private continueGame(): void {
    this.startCave();
    this.playMusic();
  }

  private labelFor(index: number): string {
    const saved = this.session;
    this.session = { ...saved, current: index };
    const label = caveForTurn(this.request, this.session).label;
    this.session = saved;
    return label;
  }

  private statusText(): { text: string | null; colour?: number } {
    if (this.banner) return { text: this.banner.text, colour: YELLOW };
    if (this.paused) return { text: 'PAUSED', colour: WHITE };
    const p = currentPlayer(this.session);
    if (this.run.introducing) {
      const r = this.request;
      if (r.mode === 'demo') return { text: 'DEMO' };
      if (r.mode === 'practice') return { text: `PRACTICE,  ${this.turn.label}` };
      if (r.mode === 'daily') return { text: `DAILY DIG ${this.turn.label}` };
      const men = `${p.lives} ${p.lives === 1 ? 'MAN' : 'MEN'}`;
      return { text: `PLAYER ${this.session.current + 1},  ${men},  ${this.turn.label}` };
    }
    if (this.run.phase === 'timeout' && Math.floor(this.run.clock / 450) % 2 === 0) return { text: 'OUT OF TIME', colour: YELLOW };
    return { text: null };
  }

  draw(screen: Screen): void {
    const opts = this.host.services.options.get();
    const p = currentPlayer(this.session);
    const oneShot = this.session.unlimited;
    drawRun(screen, this.run, opts, { ...this.statusText(), score: oneShot ? p.score - this.scoreBase : p.score });
    if (this.paused) drawPause(screen, this.items, this.pauseIndex, this.run.anim);
    if (this.banner) drawBanner(screen, this.banner.text, this.banner.sub, this.run.anim);
  }

  action(a: MenuAction): void {
    if (this.request.mode === 'demo') return void this.host.go({ to: 'title', skipPress: true });
    if (this.banner) {
      if (a === 'confirm' && this.banner.ms < BANNER_MS - 600) this.banner.ms = 0;
      return;
    }
    if (!this.paused) {
      if (a === 'pause' || a === 'back') this.setPaused(true);
      else if (a === 'confirm') this.run.confirm();
      return;
    }
    if (a === 'up') this.pauseIndex = (this.pauseIndex + this.items.length - 1) % this.items.length;
    else if (a === 'down') this.pauseIndex = (this.pauseIndex + 1) % this.items.length;
    else if (a === 'back' || a === 'pause') this.setPaused(false);
    else if (a === 'confirm') this.choose(this.items[this.pauseIndex]?.id ?? 'resume');
    if (a === 'up' || a === 'down') this.deps.audio.ui('move');
  }

  pointer(p: PointerInput): void {
    if (this.request.mode === 'demo' && p.kind === 'down') return void this.host.go({ to: 'title', skipPress: true });
    if (!this.paused) return;
    const i = pauseHit({ width: this.size().w, height: 200 }, this.items, p.x, p.y);
    if (i >= 0 && p.kind === 'move') this.pauseIndex = i;
    if (i >= 0 && p.kind === 'down') {
      this.pauseIndex = i;
      this.choose(this.items[i]?.id ?? 'resume');
    }
  }

  private setPaused(on: boolean): void {
    this.paused = on;
    this.pauseIndex = 0;
    this.deps.latch.reset();
    this.deps.audio.cue(on ? 'pause' : 'resume');
    this.deps.audio.ambience({ amoeba: false, magic: false, cover: false });
    this.deps.touch?.(!on);
  }

  private choose(id: PauseItem['id']): void {
    this.deps.audio.ui('select');
    if (id === 'resume') this.setPaused(false);
    else if (id === 'options') this.host.push({ to: 'options' });
    else if (id === 'restart') {
      this.setPaused(false);
      this.forced = 'died';
      this.run.startOutro();
    } else if (id === 'quit') {
      this.setPaused(false);
      const r = this.request;
      if (r.mode === 'practice') void (r.back === 'pop' ? this.host.back() : this.host.go(r.back));
      else if (this.session.unlimited) this.host.go({ to: 'title', skipPress: true });
      else {
        this.recorder?.endAttempt('quit');
        this.finish();
      }
    }
  }
}
